import { ref, watch, type Ref } from 'vue';
import { useConvexQuery } from 'convex-vue';
import type { FunctionReference, FunctionArgs, FunctionReturnType } from 'convex/server';

interface StaleConnectionState {
  isReconnecting: Ref<boolean>;
  connectionLost: Ref<boolean>;
}

// Global state to track if any query detected a stale connection
const globalIsReconnecting = ref(false);

// Event name for stale connection detection
const STALE_CONNECTION_EVENT = 'convex:stale-connection';

/**
 * Wraps useConvexQuery with stale connection detection.
 * When a query that previously had data suddenly returns null/empty,
 * it dispatches a custom event that the app can listen to for auth refresh.
 */
export function useConvexQueryWithStaleDetection<FuncRef extends FunctionReference<'query'>>(
  funcRef: FuncRef,
  getArgs: () => FunctionArgs<FuncRef>,
  options?: {
    // Function to determine if data is considered "empty" (stale detection triggers on transition from non-empty to empty)
    isEmpty?: (data: FunctionReturnType<FuncRef> | undefined) => boolean;
    // Component name for logging
    name?: string;
  }
): ReturnType<typeof useConvexQuery<FuncRef>> & StaleConnectionState {
  const hadData = ref(false);
  const isReconnecting = ref(false);
  const connectionLost = ref(false);

  const query = useConvexQuery(funcRef, getArgs);

  const checkIsEmpty = (data: unknown): boolean => {
    if (options?.isEmpty) {
      return options.isEmpty(data as FunctionReturnType<FuncRef>);
    }
    // Default: null, undefined, or empty arrays are considered empty
    if (data === null || data === undefined) return true;
    if (Array.isArray(data) && data.length === 0) return true;
    return false;
  };

  const handleStaleConnection = () => {
    const componentName = options?.name || 'Component';
    console.log(`[${componentName}] Detected stale connection, dispatching event...`);

    // Set global state
    globalIsReconnecting.value = true;
    isReconnecting.value = true;

    // Dispatch custom event - main.ts will handle the auth refresh
    window.dispatchEvent(
      new CustomEvent(STALE_CONNECTION_EVENT, {
        detail: { componentName },
      })
    );
  };

  watch(
    () => query.data.value,
    (newData, oldData) => {
      const wasEmpty = checkIsEmpty(oldData);
      const isNowEmpty = checkIsEmpty(newData);

      if (!isNowEmpty) {
        // We have data now
        hadData.value = true;
        isReconnecting.value = false;
        connectionLost.value = false;
        globalIsReconnecting.value = false;
      } else if (!wasEmpty && isNowEmpty && hadData.value) {
        // Transition from having data to empty - likely stale connection
        handleStaleConnection();
      }
    },
    { immediate: true }
  );

  return {
    ...query,
    isReconnecting,
    connectionLost,
  };
}

/**
 * Returns the global reconnection state.
 * Useful for showing a global "reconnecting" indicator.
 */
export function useGlobalConnectionState() {
  return {
    isReconnecting: globalIsReconnecting,
  };
}

// Export the event name so main.ts can use it
export { STALE_CONNECTION_EVENT };
